import { InfiniteListBase, useGetMany, useListContext } from "ra-core";
import { CalendarClock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import type { Sale } from "../types";
import { formatDateTime } from "../misc/DateTimeStamp";
import { useConfigurationContext } from "../root/ConfigurationContext";

/**
 * Meetings this contact booked through a teammate's booking page
 * (apps/services modules/booking writes them). Same shape as the form-fills
 * card: renders nothing when there are none, and fails quietly in the demo,
 * which has no meetings collection.
 */

interface MeetingRecord {
  id: string;
  sales_id: number;
  starts_at: string;
  ends_at: string;
  title: string;
  notes: string | null;
  status: "booked" | "canceled";
}

const MeetingsCard = () => {
  const { timezone } = useConfigurationContext();
  const { data, isPending } = useListContext<MeetingRecord>();
  const hostIds = [...new Set((data ?? []).map((m) => m.sales_id))];
  const { data: hosts } = useGetMany<Sale>(
    "sales",
    { ids: hostIds },
    { enabled: hostIds.length > 0 },
  );
  if (isPending || !data || data.length === 0) return null;

  const hostName = (id: number) => {
    const s = hosts?.find((h) => h.id === id);
    return s ? `${s.first_name} ${s.last_name}`.trim() : "";
  };
  const now = Date.now();

  return (
    <Card>
      <CardContent>
        <h2 className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium mb-3">
          Meetings
        </h2>
        <div className="flex flex-col gap-2.5">
          {data.map((m) => {
            const upcoming = Date.parse(m.starts_at) > now;
            const host = hostName(m.sales_id);
            return (
              <div
                key={m.id}
                className="flex gap-2 text-sm border-t border-border pt-2.5 first:border-t-0 first:pt-0"
              >
                <CalendarClock className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium truncate">
                      {m.title}
                      {host ? ` with ${host}` : ""}
                    </span>
                    <span className="text-xs text-muted-foreground shrink-0 ml-auto">
                      {m.status === "canceled"
                        ? "Canceled"
                        : upcoming
                          ? "Upcoming"
                          : "Past"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {formatDateTime(m.starts_at, timezone)}
                  </p>
                  {m.notes && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                      {m.notes}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export const ContactMeetings = ({
  contactId,
}: {
  contactId: number | string;
}) => (
  <InfiniteListBase
    resource="meetings"
    filter={{ contact_id: contactId }}
    sort={{ field: "starts_at", order: "DESC" }}
    perPage={10}
    disableSyncWithLocation
    storeKey={false}
    loading={false}
    error={false}
    queryOptions={{
      onError: () => {
        // The demo/FakeRest provider (and a workspace without the meeting
        // scheduler migration) has no meetings collection - render nothing.
      },
    }}
  >
    <MeetingsCard />
  </InfiniteListBase>
);
